/**
 * EducationSection
 *
 * Renders the list of education entries.
 * Read-only when `onEdit` is omitted.
 *
 * Each entry's headline comes from `educationHeadline` (`@tonnie/onboarding`):
 * a degree the form offered is shown in the reader's language, a free-text
 * degree from a CV import as written, joined to the field by the locale's own
 * phrasing — never the stored slug ("bachelor").
 */

import { educationHeadline } from '@tonnie/onboarding';
import { useLocale, useTranslation } from '@tonnie/locale';
import { GraduationCap, Calendar } from '@tonnie/assets';
import { ProfileSectionCard } from '../ProfileSectionCard';
import { formatPeriod } from '../period';
import { IOnboardingData, ProfileReviewEditHandler } from '../types';

interface IEducationSectionProps {
  data: NonNullable<IOnboardingData['education']>;
  onEdit?: ProfileReviewEditHandler;
}

export function EducationSection({ data, onEdit }: IEducationSectionProps) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();

  return (
    <ProfileSectionCard
      icon={GraduationCap}
      title={t('profile.educationCount', { count: data.length })}
      description={t('profile.educationalBackground')}
      stepId="education"
      onEdit={onEdit}
    >
      {data.map((edu, idx) => {
        const headline = educationHeadline(t, edu);
        // "Jan 1995 – Jan 1999", never the stored "1995-01 - 1999-01".
        const period = formatPeriod(edu.startDate, edu.endDate, formatDate, t('profile.ongoing'));
        return (
          <div key={idx} className="p-4 bg-muted/30 rounded-lg border border-border/50 space-y-2">
            {headline && <p className="text-base font-semibold text-foreground">{headline}</p>}
            <p className="text-sm font-medium text-muted-foreground">{edu.institution}</p>
            {period && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground tabular-nums">
                <Calendar className="w-3.5 h-3.5" aria-hidden="true" />
                <span data-testid="profile-education-period">{period}</span>
              </div>
            )}
          </div>
        );
      })}
    </ProfileSectionCard>
  );
}
